import { useTranslation } from 'react-i18next';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import { Alert } from 'react-daisyui';
import ChevronRightIcon from '@/components/ui/icons/ChevronRightIcon.tsx';
import WhiteBlock from '@/components/ui/WhiteBlock/WhiteBlock.tsx';
import StarListElement from '@/components/ui/StarListElement/StarListElement.tsx';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import useStartTrial from '@/api/userFeatures/useStartTrial.ts';
import useProducts from '@/api/useProducts.ts';
import IconListElement from '@/components/ui/IconListElement/IconListElement.tsx';
import { CalendarDaysIcon, HeartIcon } from '@heroicons/react/24/outline';
import Separator from '@/components/ui/Separator/Separator.tsx';

function EarlyAccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync: startTrial, error: errorStartTrial, isPending: isLoadingStartTrial } = useStartTrial();
  const {
    data: products,
    error: errorLoadingProducts,
    isLoading: isLoadingProducts,
  } = useProducts(true, {
    price_id: 'trial',
  });

  const product = products?.[0] ?? null;

  const trialLength = product?.interval_count ?? 3;

  const handleStartTrial = async () => {
    try {
      if (isLoadingStartTrial) return;

      await startTrial();

      navigate(Routes.DASHBOARD);
    } catch (error) {
      console.error('Error starting trial:', error);
    }
  };

  return (
    <LoaderWrapper isLoading={isLoadingProducts} className="h-[100dvh]">
      <BrandCardSideContent>
        <div className="flex flex-col w-full justify-center items-center">
          <div className="w-[416px] max-w-full flex flex-col gap-4">
            {errorStartTrial ? <Alert status="error">{errorStartTrial.message}</Alert> : null}
            {errorLoadingProducts ? <Alert status="error">{errorLoadingProducts.message}</Alert> : null}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col mb-4">
                <h1 className="font-feature font-bold text-4xl">{t('earlyAccessPage.header')}</h1>
                <h2 className="font-feature italic font-bold text-4.5xl text-primary">
                  {t('earlyAccessPage.header_2')}
                </h2>
              </div>
              <p className="font-medium">{t('earlyAccessPage.subheader')}</p>
              <p className="text-sm text-text-secondary">{t('earlyAccessPage.body')}</p>
            </div>
            <WhiteBlock.Wrapper>
              <StarListElement>
                <div className="font-medium">{t('earlyAccessPage.feature1')}</div>
                <div className="text-sm text-text-secondary">{t('earlyAccessPage.feature1_desc')}</div>
              </StarListElement>
              <StarListElement>
                <div className="font-medium">{t('earlyAccessPage.feature2')}</div>
                <div className="text-sm text-text-secondary">{t('earlyAccessPage.feature2_desc')}</div>
              </StarListElement>
              <StarListElement>
                <div className="font-medium">{t('earlyAccessPage.feature3')}</div>
                <div className="text-sm text-text-secondary">{t('earlyAccessPage.feature3_desc')}</div>
              </StarListElement>
            </WhiteBlock.Wrapper>
            <div className="flex flex-col gap-3">
              <LoadingButton
                loading={isLoadingStartTrial}
                type="button"
                color="primary"
                endIcon={<ChevronRightIcon className="w-3 h-3" />}
                onClick={handleStartTrial}
              >
                {t('button.continue_exploring', { number: trialLength })}
              </LoadingButton>
              <div className="flex flex-col gap-4">
                <IconListElement icon={HeartIcon}>
                  <span className="text-text-secondary text-sm">{t('earlyAccessPage.feedback')}</span>
                </IconListElement>
                <Separator />
                <IconListElement icon={CalendarDaysIcon}>
                  <div className="font-medium">{t('earlyAccessPage.early_limited')}</div>
                  <div className="text-text-secondary text-sm">{t('earlyAccessPage.early_limited_2')}</div>
                </IconListElement>
              </div>
            </div>
          </div>
        </div>
      </BrandCardSideContent>
    </LoaderWrapper>
  );
}

export default EarlyAccessPage;
